// AUD-4 — the generative music system. A seeded ambient composer picks a scale/mode
// per game context (menu / overworld day-night / nether / end / creative), walks a
// sparse melody over a pad with a small Markov phrase structure, and emits a
// deterministic note list the engine schedules. The 5 jukebox discs are fixed-seed
// compositions with distinct params + a fixed comparator output. PURE — no WebAudio.
import { Rng } from '../../../sim/engine/rng';
import type { Patch } from './patch';

export type MusicContext = 'menu' | 'overworld_day' | 'overworld_night' | 'nether' | 'end' | 'creative';

/** Diatonic/mode interval sets (semitones from the root). */
const SCALES: Record<string, readonly number[]> = {
  major: [0, 2, 4, 5, 7, 9, 11],
  minor: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  pentatonic: [0, 2, 4, 7, 9],
};

interface ContextParams { readonly rootHz: number; readonly scale: keyof typeof SCALES; readonly tempoBpm: number; readonly seedSalt: string; }

const CONTEXT_PARAMS: Record<MusicContext, ContextParams> = {
  menu: { rootHz: 261.63, scale: 'major', tempoBpm: 70, seedSalt: 'ctx.menu' },
  overworld_day: { rootHz: 293.66, scale: 'major', tempoBpm: 82, seedSalt: 'ctx.day' },
  overworld_night: { rootHz: 246.94, scale: 'minor', tempoBpm: 64, seedSalt: 'ctx.night' },
  nether: { rootHz: 220.0, scale: 'phrygian', tempoBpm: 60, seedSalt: 'ctx.nether' },
  end: { rootHz: 261.63, scale: 'dorian', tempoBpm: 56, seedSalt: 'ctx.end' },
  creative: { rootHz: 329.63, scale: 'pentatonic', tempoBpm: 92, seedSalt: 'ctx.creative' },
};

/** Pick the music context from world state (D-audio context table). */
export function contextFor(dimension: string, dayTime: number, gameMode: string): MusicContext {
  if (gameMode === 'creative') return 'creative';
  if (dimension === 'nether') return 'nether';
  if (dimension === 'end') return 'end';
  const t = ((dayTime % 24000) + 24000) % 24000;
  return t >= 13000 && t < 23000 ? 'overworld_night' : 'overworld_day';
}

/** One scheduled note in a composition. */
export interface MusicNote {
  readonly timeMs: number;
  readonly freq: number;
  readonly durationMs: number;
  readonly voice: 'lead' | 'pad';
}

const midiToHz = (semitonesFromRoot: number, rootHz: number): number => rootHz * 2 ** (semitonesFromRoot / 12);

/** Compose a deterministic phrase from a scale/tempo + seed (Markov degree walk +
 *  a held pad root per bar). Same params + seed ⇒ identical note list. */
export function compose(rootHz: number, scaleName: keyof typeof SCALES, tempoBpm: number, seed: number | string, bars = 4): MusicNote[] {
  const scale = SCALES[scaleName]!;
  const rng = new Rng(seed);
  const beatMs = 60000 / tempoBpm;
  const barMs = beatMs * 4;
  const notes: MusicNote[] = [];
  let degree = 0; // index within an extended two-octave scale

  for (let bar = 0; bar < bars; bar++) {
    // Pad: hold the root (an octave down) across the bar.
    notes.push({ timeMs: bar * barMs, freq: midiToHz(scale[0]! - 12, rootHz), durationMs: barMs * 0.98, voice: 'pad' });
    // Lead: up to 4 sparse beats; each beat may rest, hold, or step.
    for (let beat = 0; beat < 4; beat++) {
      const roll = rng.nextFloat();
      if (roll < 0.28) continue; // rest — sparseness
      // Markov step: mostly stepwise, occasionally a leap, occasionally repeat.
      const move = rng.nextFloat();
      degree += move < 0.45 ? (rng.nextFloat() < 0.5 ? 1 : -1)
        : move < 0.6 ? (rng.nextFloat() < 0.5 ? 2 : -2)
        : 0;
      degree = Math.max(0, Math.min(scale.length * 2 - 1, degree));
      const semi = scale[degree % scale.length]! + 12 * Math.floor(degree / scale.length);
      notes.push({
        timeMs: bar * barMs + beat * beatMs,
        freq: midiToHz(semi, rootHz),
        durationMs: beatMs * (rng.nextFloat() < 0.3 ? 1.8 : 0.9),
        voice: 'lead',
      });
    }
  }
  return notes;
}

/** Compose the ambient phrase for a game context. */
export function composeContext(context: MusicContext, seed: number | string = 'music'): MusicNote[] {
  const p = CONTEXT_PARAMS[context];
  return compose(p.rootHz, p.scale, p.tempoBpm, `${p.seedSalt}.${seed}`);
}

// ── Jukebox discs (AUD-4) ────────────────────────────────────────────────────

export interface DiscDef {
  readonly id: string;
  /** Comparator output when this disc plays in a jukebox (1..15, Minecraft-style). */
  readonly comparator: number;
  readonly rootHz: number;
  readonly scale: keyof typeof SCALES;
  readonly tempoBpm: number;
  readonly seed: number;
}

/** The 5 discs — fixed-seed compositions with distinct params (placeholder ids). */
export const DISCS: readonly DiscDef[] = [
  { id: 'music_disc_01', comparator: 1, rootHz: 261.63, scale: 'pentatonic', tempoBpm: 96, seed: 1010 },
  { id: 'music_disc_02', comparator: 4, rootHz: 246.94, scale: 'minor', tempoBpm: 72, seed: 2020 },
  { id: 'music_disc_03', comparator: 7, rootHz: 293.66, scale: 'dorian', tempoBpm: 84, seed: 3030 },
  { id: 'music_disc_04', comparator: 10, rootHz: 220.0, scale: 'phrygian', tempoBpm: 60, seed: 4040 },
  { id: 'music_disc_05', comparator: 13, rootHz: 329.63, scale: 'major', tempoBpm: 108, seed: 5050 },
];

const DISC_BY_ID = new Map(DISCS.map((d) => [d.id, d]));
export function discById(id: string | null | undefined): DiscDef | null {
  return id != null ? DISC_BY_ID.get(id) ?? null : null;
}

/** The comparator output for a jukebox holding this disc (0 when empty/unknown). */
export function discComparatorOutput(discId: string | null | undefined): number {
  return discById(discId)?.comparator ?? 0;
}

/** Compose a disc's fixed composition (8 bars). */
export function composeDisc(id: string): MusicNote[] {
  const d = discById(id);
  if (!d) return [];
  return compose(d.rootHz, d.scale, d.tempoBpm, d.seed, 8);
}

// ── Music voices (the engine renders these per note) ─────────────────────────

/** The two composer voices. `freq` is the reference; the engine pitches each note
 *  by `note.freq / voice.freq`. */
export const MUSIC_VOICES: Record<'lead' | 'pad', Patch> = {
  lead: {
    wave: 'triangle', freq: 440, durationMs: 600,
    env: { attackMs: 6, decayMs: 200, sustain: 0.25, releaseMs: 320 }, gain: 0.26,
    filter: { mode: 'lowpass', startHz: 3000, endHz: 1400, q: 0.8 }, detuneCents: 4,
  },
  pad: {
    wave: 'saw', freq: 220, durationMs: 1600,
    env: { attackMs: 300, decayMs: 400, sustain: 0.5, releaseMs: 600 }, gain: 0.12,
    filter: { mode: 'lowpass', startHz: 900, endHz: 500, q: 0.7 }, detuneCents: 8,
  },
};
